// src/App.tsx - OPTIMIZED WITH PERFORMANCE IMPROVEMENTS
import React, { useEffect, lazy, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Header from './components/shared/Header';
import { ProvidersWrapper } from './components/providers/ProvidersWrapper';
import LoadingSpinner from './components/shared/LoadingSpinner';
import './App.css';

// Lazy load the preload function to avoid blocking initial render
const preloadDashboard = () => {
  // Use requestIdleCallback for non-critical preloading
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => {
      // Preload customer dashboard components
      import('./pages/Dashboard/CustomerDashboard');
      import('./pages/Dashboard/CustomerOrders');
      import('./pages/Dashboard/CustomerPayments');
      import('./pages/Dashboard/CustomerProfile');
      import('./pages/Dashboard/RiderDashboard');
      import('./pages/Dashboard/RiderOrders');
      import('./pages/Dashboard/RiderEarnings');
      import('./pages/Dashboard/RiderProfile');
    });
  } else {
    setTimeout(() => {
      import('./pages/Dashboard/CustomerDashboard');
      import('./pages/Dashboard/RiderDashboard');
    }, 3000);
  }
};

// Preload marketplace components for faster navigation
const preloadMarketplace = () => {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => {
      import('./pages/Marketplace/Marketplace');
      import('./pages/Marketplace/CreateListing');
    });
  }
};

function App() {
  const location = useLocation();
  
  // Show header ONLY on homepage (path === '/')
  const showHeader = location.pathname === '/';
  
  // Preload components after initial render
  useEffect(() => {
    // Preload dashboard components after a short delay
    const dashboardTimer = setTimeout(() => {
      const token = localStorage.getItem('token');
      if (token) {
        preloadDashboard();
      } else {
        // Preload marketplace for non-authenticated users
        preloadMarketplace();
      }
    }, 2000);
    
    return () => clearTimeout(dashboardTimer);
  }, []);
  
  // Prefetch on hover for navigation links
  useEffect(() => {
    const handleMouseEnter = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      if (link && link.href) {
        const href = link.getAttribute('href');
        if (href === '/dashboard/customer' || href === '/dashboard/rider') {
          preloadDashboard();
        } else if (href === '/marketplace') {
          preloadMarketplace();
        }
      }
    };
    
    document.addEventListener('mouseenter', handleMouseEnter, true);
    return () => document.removeEventListener('mouseenter', handleMouseEnter, true);
  }, []);

  return (
    <ProvidersWrapper>
      <div className="min-h-screen bg-gray-50">
        {showHeader && <Header />}
        <main className={`container mx-auto px-4 py-8 ${!showHeader ? 'pt-0' : ''}`}>
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center">
              <LoadingSpinner size="lg" />
            </div>
          }>
            <Outlet />
          </Suspense>
        </main>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#363636',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              borderRadius: '12px',
              padding: '12px 16px',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </div>
    </ProvidersWrapper>
  );
}

export default App;